/**
 * src/pages/ClienteDetalle.jsx
 * Vista detalle de un cliente: info, empresas propias, competidores, scheduler.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input, Toggle, Select } from '../components/ui/Input'
import { Table } from '../components/ui/Table'
import {
  useCliente, useUpdateCompetidor, useUpdateEmpresa, useUpdateScheduler, useAddCompetidor,
} from '../hooks/useClientes'
import api from '../api/client'

const FREQ_OPTS = [
  { value: '24h', label: 'Cada 24h' },
  { value: '48h', label: 'Cada 48h' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'manual', label: 'Manual' },
]

function TabBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px', fontSize: '13px', fontWeight: active ? 500 : 400,
        color: active ? 'var(--color-accent)' : 'var(--color-text-3)',
        background: 'none', border: 'none', cursor: 'pointer',
        borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
        transition: 'all 0.15s', fontFamily: 'var(--font-ui)',
      }}
    >
      {label}
    </button>
  )
}

function InfoTab({ cliente }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
      {[
        ['ID', cliente.cliente_id],
        ['Nombre', cliente.nombre],
        ['Email', cliente.email || '—'],
        ['Tier', <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>],
        ['Familia CM', cliente.familia_cm],
        ['Estado', <Badge color={cliente.activo ? 'green' : 'gray'} dot>{cliente.activo ? 'Activo' : 'Inactivo'}</Badge>],
      ].map(([label, value]) => (
        <div key={label} style={{
          padding: '12px 14px', background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: '8px',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '5px' }}>
            {label}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--color-text)' }}>{value}</div>
        </div>
      ))}
      <div style={{ gridColumn: '1/-1', padding: '12px 14px', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
        <div style={{ fontSize: '10px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '5px' }}>URL BASE</div>
        <div style={{ fontSize: '12px', color: 'var(--color-text)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{cliente.url_base}</div>
      </div>
    </div>
  )
}

function EmpresasTab({ empresas, clienteId }) {
  const updateMutation = useUpdateEmpresa(clienteId)
  const [editando, setEditando] = useState(null)   // empresa en edición | null
  const [form, setForm] = useState({ rut: '', nombre: '', seller_code: '' })
  const [verificando, setVerificando] = useState(false)
  const [hallazgo, setHallazgo] = useState(null)   // {empresa, seller_code} | {error}
  const [saveError, setSaveError] = useState('')
  const [savedId, setSavedId] = useState(null)

  function abrirEdicion(emp) {
    setEditando(emp)
    setForm({ rut: emp.rut ?? '', nombre: emp.nombre ?? '', seller_code: emp.seller_code ?? '' })
    setHallazgo(null)
    setSaveError('')
  }

  function cerrarEdicion() {
    setEditando(null)
    setHallazgo(null)
    setSaveError('')
  }

  // Verifica el RUT contra Mercado Público y autocompleta nombre + seller_code
  // (mismo endpoint que usa el wizard de onboarding).
  async function verificarRut() {
    const rut = form.rut.trim()
    if (!rut) { setHallazgo({ error: 'Escribe el RUT primero.' }); return }
    setVerificando(true); setHallazgo(null)
    try {
      const { data } = await api.get('/api/diagnostico/confirmar', { params: { rut } })
      if (data?.encontrada && data?.seller_code) {
        setForm(f => ({
          ...f,
          rut:         data.rut || f.rut,
          nombre:      data.empresa || f.nombre,
          seller_code: String(data.seller_code),
        }))
        setHallazgo({ empresa: data.empresa, seller_code: data.seller_code })
      } else {
        setHallazgo({ error: 'No encontramos esa empresa como proveedor del Estado. Revisa el RUT.' })
      }
    } catch (err) {
      setHallazgo({ error: 'No se pudo verificar el RUT ahora. Inténtalo de nuevo.' })
    } finally { setVerificando(false) }
  }

  async function guardar() {
    setSaveError('')
    const data = {
      rut:         form.rut.trim(),
      nombre:      form.nombre.trim(),
      seller_code: form.seller_code.trim(),
    }
    if (!data.rut || !data.nombre || !data.seller_code) {
      setSaveError('RUT, nombre y seller code son obligatorios.')
      return
    }
    try {
      const empId = editando.id
      await updateMutation.mutateAsync({ empId, data })
      cerrarEdicion()
      setSavedId(empId)
      setTimeout(() => setSavedId(null), 2500)
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'No se pudo guardar. Inténtalo de nuevo.')
    }
  }

  const COLS = [
    { key: 'rut', label: 'RUT', mono: true },
    { key: 'nombre', label: 'Nombre' },
    { key: 'seller_code', label: 'Seller Code', mono: true },
    {
      key: 'acciones', label: '', width: '90px', align: 'right',
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {savedId === row.id && (
            <span style={{ fontSize: '11px', color: 'var(--color-ok)', fontFamily: 'var(--font-mono)' }}>✓</span>
          )}
          <Button
            size="sm" variant="secondary"
            icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>}
            onClick={() => abrirEdicion(row)}
          >
            Editar
          </Button>
        </span>
      ),
    },
  ]

  return (
    <div>
      <Table columns={COLS} data={empresas} emptyMessage="Sin empresas propias" />

      {editando && (
        <div style={{
          marginTop: '16px', padding: '16px',
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: '8px',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '14px' }}>
            EDITAR EMPRESA · #{editando.id}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <Input
                label="RUT"
                value={form.rut}
                onChange={e => setForm(f => ({ ...f, rut: e.target.value }))}
                placeholder="76.000.000-0"
                style={{ flex: 1 }}
              />
              <Button size="md" variant="secondary" onClick={verificarRut} loading={verificando}>
                Verificar
              </Button>
            </div>
            <Input
              label="NOMBRE"
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Razón social"
            />
            <Input
              label="SELLER CODE"
              value={form.seller_code}
              onChange={e => setForm(f => ({ ...f, seller_code: e.target.value }))}
              placeholder="Código proveedor MP"
            />
          </div>

          {hallazgo?.empresa && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-ok)' }}>
              ✓ {hallazgo.empresa} · código {hallazgo.seller_code}
            </div>
          )}
          {hallazgo?.error && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-error)' }}>
              {hallazgo.error}
            </div>
          )}
          {saveError && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-error)' }}>
              {saveError}
            </div>
          )}

          <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
            <Button onClick={guardar} loading={updateMutation.isPending}>
              Guardar cambios
            </Button>
            <Button variant="ghost" onClick={cerrarEdicion} disabled={updateMutation.isPending}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function CompetidoresTab({ competidores, clienteId }) {
  const updateMutation = useUpdateCompetidor(clienteId)

  const COLS = [
    { key: 'rut', label: 'RUT', mono: true },
    { key: 'nombre', label: 'Nombre' },
    { key: 'seller_code', label: 'Seller Code', mono: true },
    {
      key: 'activo', label: 'Activo', width: '70px', align: 'center',
      render: (v, row) => (
        <Toggle
          checked={v}
          onChange={(val) => updateMutation.mutate({ compId: row.id, data: { activo: val } })}
        />
      ),
    },
  ]
  return <Table columns={COLS} data={competidores} emptyMessage="Sin competidores configurados" />
}

function SchedulerTab({ scheduler, clienteId }) {
  const updateMutation = useUpdateScheduler(clienteId)
  const [local, setLocal] = useState({
    activo_precios: scheduler?.activo_precios ?? false,
    freq_precios: scheduler?.freq_precios ?? '48h',
    activo_ocs: scheduler?.activo_ocs ?? false,
    freq_ocs: scheduler?.freq_ocs ?? 'semanal',
  })
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  async function handleSave() {
    setError(null)
    try {
      await updateMutation.mutateAsync(local)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar la configuración')
    }
  }

  function Row({ label, activeKey, freqKey }) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 0', borderBottom: '1px solid var(--color-border)',
      }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)' }}>{label}</div>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginTop: '2px' }}>
            {local[activeKey] ? `Activo · ${local[freqKey]}` : 'Inactivo'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Select
            value={local[freqKey]}
            onChange={e => setLocal(l => ({ ...l, [freqKey]: e.target.value }))}
            options={FREQ_OPTS}
            style={{ width: '130px' }}
          />
          <Toggle
            checked={local[activeKey]}
            onChange={val => setLocal(l => ({ ...l, [activeKey]: val }))}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <Row label="Scraping de precios" activeKey="activo_precios" freqKey="freq_precios" />
      <Row label="Scraping de OCs" activeKey="activo_ocs" freqKey="freq_ocs" />
      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Guardar configuración
        </Button>
        {saved && (
          <span style={{ fontSize: '12px', color: 'var(--color-ok)', fontFamily: 'var(--font-mono)' }}>
            ✓ Guardado
          </span>
        )}
        {error && (
          <span style={{ fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>
            {error}
          </span>
        )}
      </div>
    </div>
  )
}

export default function ClienteDetalle() {
  const { id: clienteId } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState('info')
  const { data: cliente, isLoading, error } = useCliente(clienteId)

  if (isLoading) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Cliente" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
        Cargando...
      </div>
    </div>
  )

  if (error || !cliente) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Cliente" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-error)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
        Cliente no encontrado
      </div>
    </div>
  )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title={cliente.nombre}
        subtitle={`${cliente.cliente_id} · ${cliente.familia_cm}`}
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="sm" variant="secondary"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
              onClick={() => navigate(`/scraping/${clienteId}`)}
            >
              Scraping
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--color-border)', marginBottom: '20px', display: 'flex', gap: '0' }}>
          {[
            { key: 'info', label: 'Información' },
            { key: 'empresas', label: `Empresas propias (${cliente.empresas_propias?.length ?? 0})` },
            { key: 'competidores', label: `Competidores (${cliente.competidores?.length ?? 0})` },
            { key: 'scheduler', label: 'Scheduler' },
          ].map(t => (
            <TabBtn key={t.key} label={t.label} active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </div>

        <Card>
          {tab === 'info'         && <InfoTab cliente={cliente} />}
          {tab === 'empresas'     && <EmpresasTab empresas={cliente.empresas_propias ?? []} clienteId={clienteId} />}
          {tab === 'competidores' && <CompetidoresTab competidores={cliente.competidores ?? []} clienteId={clienteId} />}
          {tab === 'scheduler'    && <SchedulerTab scheduler={cliente.scheduler_config} clienteId={clienteId} />}
        </Card>
      </div>
    </div>
  )
}
